/**
 * LinkCheckerService.js
 * Client-side service for collecting links from websites and sending them to the server for status checking
 */

class LinkCheckerService {
  constructor() {
    const apiPort = process.env.REACT_APP_API_PORT || '5002';
    this.apiBaseUrl = process.env.REACT_APP_API_URL || `http://localhost:${apiPort}/api`;
    this.proxyCache = new Map(); // Cache for HTML content fetching
    
    // CORS proxies to try in sequence when direct fetch fails (for HTML fetch only)
    this.corsProxies = [
      'https://api.allorigins.win/raw?url=',
      'https://corsproxy.io/?',
      'https://cors-anywhere.herokuapp.com/'
    ];
  }

  /**
   * Collect links from a webpage and then check them via the server
   * @param {string} url - The URL to check
   * @param {object} options - Options for link checking
   * @returns {Promise<Object>} - Results of link checking
   */
  async checkLinks(url, options = {}) {
    try {
      console.log(`[LinkCheckerService] Starting link collection for ${url}`);
      
      const {
        maxLinks = 100,
        timeout = 8000,
      } = options;
      
      // Extract links from the HTML
      const links = await this._extractLinks(url, maxLinks);
      console.log(`[LinkCheckerService] Found ${links.length} links to check`);
      
      // Send the links to the server for checking
      const brokenLinks = await this._sendLinksToServer(url, links, timeout);
      
      console.log(`[LinkCheckerService] Link check completed. ${brokenLinks.length} broken links found out of ${links.length} checked.`);
      
      return {
        url,
        totalChecked: links.length,
        links: brokenLinks,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error(`[LinkCheckerService] Error in checkLinks: ${error.message}`);
      return {
        url,
        totalChecked: 0,
        links: [],
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Extract links from a webpage
   * @private
   * @param {string} url - The URL to analyze
   * @param {number} maxLinks - Maximum number of links to extract
   * @returns {Promise<Array>} - Array of link objects
   */
  async _extractLinks(url, maxLinks) {
    try {
      // Get HTML content using multiple methods to avoid CORS issues
      const html = await this._fetchHtmlContent(url);
      
      // Create a DOM parser
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find all links
      const links = [];
      const checkedUrls = new Set();
      
      // Process <a> tags
      const linkElements = doc.querySelectorAll('a[href]');
      
      // Convert the NodeList to Array and process only up to maxLinks
      Array.from(linkElements).slice(0, maxLinks).forEach(element => {
        const href = element.getAttribute('href');
        if (!href) return;
        
        try {
          // Convert relative URLs to absolute
          let absoluteUrl;
          try {
            absoluteUrl = new URL(href, url).href;
          } catch (e) {
            // Skip invalid URLs
            return;
          }
          
          // Skip duplicate URLs
          if (checkedUrls.has(absoluteUrl)) return;
          checkedUrls.add(absoluteUrl);
          
          // Skip mailto: links, tel: links, and javascript: links
          if (absoluteUrl.startsWith('mailto:') || 
              absoluteUrl.startsWith('tel:') ||
              // eslint-disable-next-line no-script-url
              absoluteUrl.startsWith('javascript:') ||
              absoluteUrl.startsWith('#')) {
            return;
          }
          
          links.push({
            url: absoluteUrl,
            text: element.textContent.trim() || absoluteUrl,
            source: url
          });
        } catch (error) {
          // Skip problematic URLs
          console.error(`[LinkCheckerService] Error processing URL ${href}:`, error.message);
        }
      });
      
      return links;
    } catch (error) {
      console.error(`[LinkCheckerService] Error extracting links: ${error.message}`);
      return [];
    }
  }

  /**
   * Fetch HTML content from a URL
   * @private
   * @param {string} url - URL to fetch
   * @returns {Promise<string>} - HTML content
   */
  async _fetchHtmlContent(url) {
    try {
      // Check cache first
      if (this.proxyCache.has(url)) {
        console.log(`[LinkCheckerService] Using cached HTML for ${url}`);
        return this.proxyCache.get(url);
      }

      // Use our server proxy API first
      console.log(`[LinkCheckerService] Trying to fetch HTML via proxy API: ${this.apiBaseUrl}/pagespeed/fetch-html`);
      const encodedUrl = encodeURIComponent(url);
      const response = await fetch(`${this.apiBaseUrl}/pagespeed/fetch-html?url=${encodedUrl}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data && data.html) {
        console.log(`[LinkCheckerService] Successfully fetched HTML via proxy API (${data.html.length} bytes)`);
        // Cache the result
        this.proxyCache.set(url, data.html);
        return data.html;
      } else {
        throw new Error('No HTML content in response');
      }
    } catch (error) {
      console.warn('[LinkCheckerService] Error fetching HTML via proxy API:', error.message);
      
      // Fallback to public CORS proxies
      try {
        console.log('[LinkCheckerService] Trying public CORS proxies...');
        
        for (const corsProxy of this.corsProxies) {
          try {
            console.log(`[LinkCheckerService] Trying CORS proxy: ${corsProxy.split('?')[0]}...`);
            const proxyUrl = `${corsProxy}${encodeURIComponent(url)}`;
            
            const response = await fetch(proxyUrl, {
              method: 'GET',
              headers: {
                'Accept': 'text/html'
              }
            });
            
            if (response.ok) {
              const html = await response.text();
              if (html && html.length > 100) { // Make sure we got meaningful content
                console.log(`[LinkCheckerService] Successfully fetched via proxy ${corsProxy.split('?')[0]} (${html.length} bytes)`);
                // Cache the result
                this.proxyCache.set(url, html);
                return html;
              }
            }
          } catch (proxyError) {
            console.warn(`[LinkCheckerService] Error with CORS proxy ${corsProxy}:`, proxyError.message);
            // Continue to next proxy
          }
        }
        
        throw new Error('All CORS proxies failed');
      } catch (fallbackError) {
        // All methods failed
        console.error('[LinkCheckerService] All HTML fetch methods failed:', fallbackError.message);
        throw new Error(`Failed to fetch HTML content: ${error.message}`);
      }
    }
  }

  /**
   * Send collected links to the server for checking
   * @private
   * @param {string} sourceUrl - Original URL being checked
   * @param {Array} links - Array of link objects to check
   * @param {number} timeout - Timeout in milliseconds
   * @returns {Promise<Array>} - Array of broken links
   */
  async _sendLinksToServer(sourceUrl, links, timeout) {
    try {
      console.log(`[LinkCheckerService] Sending ${links.length} links to server for checking`);
      
      const response = await fetch(`${this.apiBaseUrl}/pagespeed/check-links-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sourceUrl,
          links,
          timeout
        })
      });
      
      if (!response.ok) {
        throw new Error(`Server API error: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result && Array.isArray(result.brokenLinks)) {
        return result.brokenLinks;
      } else {
        console.warn('[LinkCheckerService] Server returned invalid response format');
        return [];
      }
    } catch (error) {
      console.error(`[LinkCheckerService] Error checking links via server: ${error.message}`);
      return [];
    }
  }
}

// Create a singleton instance
const linkCheckerService = new LinkCheckerService();
export default linkCheckerService; 